import './NotificationsPreferences.scss'

import { useCharacters } from '../../../contexts/Characters'
import { useToaster } from '../../../contexts/Toaster'
import { useUser } from '../../../contexts/User'
import { useData } from '../../../contexts/Data'
import { Roles } from '../../../utilities/constants'
import koalition from '../../../api/koalition'
import useFormBuilder from '../../../hooks/FormBuilderHook'
import Checkbox from '../../forms/Checkbox/Checkbox'
import DateTimePicker from '../../forms/DateTimePicker/DateTimePicker'

const NotificationsPreferences = () => {
    const user = useUser()
    const characters = useCharacters()
    const toaster = useToaster()
    const data = useData()
    const notificationsPreferences = user.$state.preferences?.notifications || {}

    const updateUserNotificationPreferences = () => {
        const formValues = form.$state.value
        formValues.excludedIntervals = formValues.excludedIntervals.filter((interval) => interval.from && interval.to)
        koalition.put('/users/preferences/notifications', formValues).then((response) => {
            user.save(response.data)
            toaster.success('Notification preferences updated successfully')
        }).catch((error) => {
            toaster.error(error)
            console.error(error)
        })
    }

    const onEnableNotificationsChange = async (status) => {
        if (status) {
            const notificationsUnsupported = !('serviceWorker' in navigator) || !('PushManager' in window)
            if (notificationsUnsupported) {
                toaster.warning(`Push notifications are not supported by your browser. If you are using iOS please open the app in Safari and install it to home screen through Safari's options`, { duration: 8000 })
            } else {
                const permission = await Notification.requestPermission()
                if (permission === 'denied') toaster.warning('You need to remove this website from blocked sites in your browser notifications settings')
                if (permission !== 'granted') setTimeout(() => form.$state.controls.enabled.setValue(false), 500)
            }
        }
    }

    const onExcludeHoursChange = (status) => {
        if (status && !form.$state.controls.excludedIntervals.instances.length) form.$state.controls.excludedIntervals.addInstance()
    }

    const intervalsValidator = {
        name: 'intervals',
        validate: (value) => {
            return !value.exclude || value.excludedIntervals?.every((interval) => interval.from && interval.to)
        },
        error: `This field must be an integer`
    }

    const allianceEventsControls = data.$state.eventTypes && Object.fromEntries(data.$state.eventTypes.alliance.map((type) => [type.name, { value: notificationsPreferences[type.name] }]))
    const serverEventsControls = data.$state.eventTypes && Object.fromEntries(data.$state.eventTypes.server.map((type) => [type.name, { value: notificationsPreferences[type.name] }]))

    const form = useFormBuilder({ onSubmit: updateUserNotificationPreferences, controls: {
        enabled: { value: notificationsPreferences.enabled },
        
        newCharacter: { value: notificationsPreferences.newCharacter },

        mgeList: { value: notificationsPreferences.mgeList },
        
        allianceEvents: { value: notificationsPreferences.allianceEvents },
        allianceEventCreation: { value: notificationsPreferences.allianceEventCreation },
        allianceEventStart: { value: notificationsPreferences.allianceEventStart },
        ...allianceEventsControls,
        
        serverEvents: { value: notificationsPreferences.serverEvents },
        serverEventCreation: { value: notificationsPreferences.serverEventCreation },
        serverEventStart: { value: notificationsPreferences.serverEventStart },
        ...serverEventsControls,

        exclude: { value: notificationsPreferences.exclude },
        excludedIntervals: {
            type: 'template',
            matrix: {
                from: { validators: [] },
                to: { validators: [] },
            },
            value: notificationsPreferences.excludedIntervals
        }
    }, validators: [intervalsValidator] })
    
    return (
        <form className="col-12 notifications-preferences" onSubmit={form.$state.onSubmit}>
            <div className="row">
                <header className="col-12 mb-4">
                    <Checkbox control={form.$state.controls.enabled} label="Enable notifications" onChange={onEnableNotificationsChange} />
                </header>
                
                <main className="col-12">
                    <div className={`card semi-dark ${form.$state.controls.enabled.value ? '' : 'disabled'} mb-4`}>
                        {characters.$state?.user?.some((character) => [Roles.developer, Roles.admin, Roles.leader].includes(character.role.name)) && (
                            <div className="mb-3">
                                <Checkbox control={form.$state.controls.newCharacter} label="A new character has just been created and needs approval" />
                            </div>
                        )}

                        <div className="mb-3">
                            <Checkbox control={form.$state.controls.mgeList} label="A fixed MGE list has been published or updated" />
                        </div>

                        <div className="mt-5 mb-4">
                            <Checkbox control={form.$state.controls.allianceEvents} label="Alliance events" />
                        </div>

                        <div className={`card ${form.$state.controls.allianceEvents.value ? '' : 'disabled'} mb-4`}>
                            <div className="mb-3">
                                <Checkbox control={form.$state.controls.allianceEventCreation} label="A new alliance event is created" />
                            </div>
                            <div className="mb-3">
                                <Checkbox control={form.$state.controls.allianceEventStart} label="An alliance event is about to start" />
                            </div>

                            <hr className="my-4" />

                            {data.$state.eventTypes?.alliance?.map((type) => (
                                <div className="mb-3" key={type.name}>
                                    <Checkbox control={form.$state.controls[type.name]} label={type.label} />
                                </div>
                            ))}
                        </div>

                        <div className="mt-5 mb-4">
                            <Checkbox control={form.$state.controls.serverEvents} label="Server events" />
                        </div>

                        <div className={`card ${form.$state.controls.serverEvents.value ? '' : 'disabled'} mb-4`}>
                            <div className="mb-3">
                                <Checkbox control={form.$state.controls.serverEventCreation} label="A new server event is created" />
                            </div>
                            <div className="mb-3">
                                <Checkbox control={form.$state.controls.serverEventStart} label="A server event is about to start" />
                            </div>

                            <hr className="my-4" />

                            {data.$state.eventTypes?.server?.map((type) => (
                                <div className="mb-3" key={type.name}>
                                    <Checkbox control={form.$state.controls[type.name]} label={type.label} />
                                </div>
                            ))}
                        </div>

                        <hr className="my-5" />

                        <div className="row flex-nowrap mb-3">
                            <div className="col">
                                <Checkbox control={form.$state.controls.exclude} label="Silence notifications in the following time intervals" onChange={onExcludeHoursChange} />
                            </div>

                            {form.$state.controls.exclude.value && (
                                <div className="col-auto">
                                    <button type="button" className="primary small icon" onClick={() => form.$state.controls.excludedIntervals.addInstance()}>
                                        <img src="/images/icons/plus.svg" />
                                    </button>
                                </div>
                            )}
                        </div>

                        {form.$state.controls.exclude.value && (
                            <div className="card base">
                                {form.$state.controls.excludedIntervals.instances.map((templateInstance, templateInstanceIndex) => (
                                    <div className={`row group align-items-center ${templateInstanceIndex ? 'mt-4' : ''}`} key={templateInstance.name}>
                                        <div className="col">
                                            <div className="row">
                                                <div className="col-sm mb-3">
                                                    <DateTimePicker control={templateInstance.controls.from} label="From" date={false} />
                                                </div>

                                                <div className="col-sm mb-3">
                                                    <DateTimePicker control={templateInstance.controls.to} label="To" date={false} />
                                                </div>
                                            </div>
                                        </div>

                                        {form.$state.controls.excludedIntervals.instances.length > 1 && (
                                            <div className="col-auto mt-3" onClick={() => form.$state.controls.excludedIntervals.removeInstance(templateInstance.name)}>
                                                <button type="button" className="primary small icon">
                                                    <img src="/images/icons/minus.svg" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>     
                </main>

                <footer className="col-12 text-right">
                    <button type="submit" className="primary large" disabled={form.$state.invalid}>Update</button>
                </footer>
            </div>
        </form>
    )
}

export default NotificationsPreferences